import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { categoryInfoBySlug, type CategoryInfo } from "@/lib/categoryInfo";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { listPublicFaqs } from "@/lib/category-faqs.functions";
import { SecurityPackages } from "@/components/site/SecurityPackages";
import { ContactCta } from "@/components/site/ContactCta";
import laadpalenCtaImg from "@/assets/laadpalen-cta.png.asset.json";
import beveiligingCtaImg from "@/assets/beveiliging-cta.png.asset.json";
import netwerkenCtaImg from "@/assets/netwerken-cta.png.asset.json";
import verlichtingCtaImg from "@/assets/verlichting-cta.png.asset.json";
import thuisbatterijCtaImg from "@/assets/thuisbatterij-cta.png.asset.json";

type InfoCtaConfig = {
  title: string;
  description: string;
  bullets: string[];
  imageUrl?: string;
  imageAlt?: string;
};

const INFO_CTA: Record<string, InfoCtaConfig> = {
  verlichting: {
    title: "Advies over LED-verlichting?",
    description:
      "Bespaar energie en verhoog comfort met professionele LED-verlichting. Wij adviseren over lichtplan, armaturen en sturing voor woning, kantoor of bedrijfspand.",
    bullets: [
      "Energiezuinige LED van topmerken",
      "Lichtplan en wattage-berekening op maat",
      "Levering én vakkundige installatie",
    ],
    imageUrl: verlichtingCtaImg.url,
    imageAlt: "Monteur installeert LED paneelverlichting in een modern kantoorpand",
  },
  beveiliging: {
    title: "Beveiliging op maat.",
    description:
      "Bescherm je woning of bedrijfspand met camerabewaking en intercoms van Hikvision en Dahua. Onze specialisten ontwerpen, leveren en installeren een systeem dat bij jouw situatie past.",
    bullets: [
      "Scherpe IP-camera's (4MP–8MP) en NVR's",
      "Camera- en lensadvies via HD & IPC tools",
      "Professionele installatie en configuratie",
    ],
    imageUrl: beveiligingCtaImg.url,
    imageAlt: "Monteur monteert een beveiligingscamera aan een gevel",
  },
  laadpalen: {
    title: "Laadpaal laten plaatsen?",
    description:
      "Van advies en vermogensberekening tot complete installatie van je laadpaal. Wij regelen het traject van A tot Z — voor thuis én voor het bedrijf.",
    bullets: [
      "Laadpalen van Alfen, Wallbox en Easee",
      "Vermogenscheck en groepenkast-uitbreiding",
      "Installatie door erkende EV-monteurs",
    ],
    imageUrl: laadpalenCtaImg.url,
    imageAlt: "Monteur installeert een laadpaal aan de gevel van een woning",
  },
  thuisbatterij: {
    title: "Leef op eigen zonnestroom.",
    description:
      "Sla je opgewekte stroom op met een thuisbatterij en gebruik 'm wanneer jij wil. Wij adviseren over capaciteit, koppeling met de omvormer en installatie.",
    bullets: [
      "Onafhankelijk advies over capaciteit en merk",
      "Koppeling met bestaande zonnepanelen",
      "Installatie inclusief inregeling en monitoring",
    ],
    imageUrl: thuisbatterijCtaImg.url,
    imageAlt: "Monteur sluit een thuisbatterij aan in een technische ruimte",
  },
  netwerken: {
    title: "Een netwerk dat altijd werkt?",
    description:
      "Bedrade netwerken, switches en wifi access points voor woning, kantoor of bedrijfspand. Wij ontwerpen en installeren een stabiel netwerk zonder dode hoeken.",
    bullets: [
      "Ubiquiti UniFi access points & switches",
      "Site survey en netwerkontwerp op maat",
      "Professionele installatie en centraal beheer",
    ],
    imageUrl: netwerkenCtaImg.url,
    imageAlt: "Monteur werkt aan een netwerkkast met patchpanelen en bekabeling",
  },
};



const SITE_URL = "https://elvion-group.nl";

export const Route = createFileRoute("/info/$slug")({
  loader: async ({ params }) => {
    const info = categoryInfoBySlug(params.slug);
    if (!info) throw notFound();
    let faqs: { q: string; a: string }[] = [];
    try {
      const res = await listPublicFaqs({ data: { slug: params.slug as any } });
      faqs = res.faqs.map((f) => ({ q: f.q, a: f.a }));
    } catch {
      faqs = info.faqs;
    }
    if (faqs.length === 0) faqs = info.faqs;
    return { info, faqs };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return {};
    const { info, faqs } = loaderData;
    const url = `${SITE_URL}/info/${params.slug}`;
    return {
      meta: [
        { title: info.metaTitle },
        { name: "description", content: info.metaDescription },
        { property: "og:title", content: info.metaTitle },
        { property: "og:description", content: info.metaDescription },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
              {
                "@type": "ListItem",
                position: 2,
                name: info.title,
                item: url,
              },
            ],
          }),
        },
      ],
    };
  },
  component: InfoPage,
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl p-10 text-center">
      <h1 className="text-2xl font-bold">Onderwerp niet gevonden</h1>
      <Link to="/" className="mt-4 inline-block text-orange hover:underline">
        Terug naar home
      </Link>
    </main>
  ),
  errorComponent: ({ error }) => (
    <main className="mx-auto max-w-3xl p-10 text-center">
      <h1 className="text-2xl font-bold">Er ging iets mis</h1>
      <p className="mt-2 text-text-secondary">{error instanceof Error ? error.message : "Onbekende fout"}</p>
    </main>
  ),
});


function InfoPage() {
  const { info, faqs } = Route.useLoaderData() as {
    info: CategoryInfo;
    faqs: { q: string; a: string }[];
  };


  return (
    <>
      <Header />
      {INFO_CTA[info.slug] && (
        <ContactCta
          title={INFO_CTA[info.slug].title}
          description={INFO_CTA[info.slug].description}
          bullets={INFO_CTA[info.slug].bullets}
          imageUrl={INFO_CTA[info.slug].imageUrl}
          imageAlt={INFO_CTA[info.slug].imageAlt}
          primaryCta={{ label: "Neem contact op", to: "/klantenservice" }}
          secondaryCta={{ label: "Offerte opvragen", to: "/offerte-aanvragen" }}
        />
      )}
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">

        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
          {info.h1}
        </h1>
        <p className="mt-4 max-w-3xl text-base text-text-secondary sm:text-lg">
          {info.intro}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          {info.slug === "beveiliging" && (
            <>
              <Link
                to="/hd-configurator"
                className="inline-flex items-center rounded-md border border-orange bg-background px-5 py-2.5 text-sm font-semibold text-orange hover:bg-orange hover:text-white"
              >
                HD Calculator
              </Link>
              <Link
                to="/cam-configurator"
                className="inline-flex items-center rounded-md border border-orange bg-background px-5 py-2.5 text-sm font-semibold text-orange hover:bg-orange hover:text-white"
              >
                IPC Configurator
              </Link>
            </>
          )}
          {info.slug === "verlichting" && (
            <Link
              to="/led-calculator"
              className="inline-flex items-center rounded-md border border-orange bg-background px-5 py-2.5 text-sm font-semibold text-orange hover:bg-orange hover:text-white"
            >
              LED Wattage Calculator
            </Link>
          )}
          {info.slug === "laadpalen" && (
            <Link
              to="/vermogen-calculator"
              className="inline-flex items-center rounded-md border border-orange bg-background px-5 py-2.5 text-sm font-semibold text-orange hover:bg-orange hover:text-white"
            >
              EV Calculator
            </Link>
          )}
          {info.slug === "groepenkasten" && (
            <Link
              to="/groepenkast-configurator"
              className="inline-flex items-center rounded-md border border-orange bg-background px-5 py-2.5 text-sm font-semibold text-orange hover:bg-orange hover:text-white"
            >
              Groepenkast Configurator
            </Link>
          )}
        </div>

        <section className="mt-12 grid gap-8 lg:grid-cols-2">
          {info.sections.map((s) => (
            <article key={s.heading}>
              <h2 className="text-xl font-bold tracking-tight">{s.heading}</h2>
              <p className="mt-2 text-text-secondary">{s.body}</p>
            </article>
          ))}
        </section>

        <section className="mt-12 grid gap-8 lg:grid-cols-2">
          <div>
            <h2 className="text-xl font-bold tracking-tight">Voordelen</h2>
            <ul className="mt-3 space-y-2">
              {info.benefits.map((b) => (
                <li
                  key={b}
                  className="flex gap-2 text-text-secondary before:mt-2 before:size-1.5 before:shrink-0 before:rounded-full before:bg-orange"
                >
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-xl font-bold tracking-tight">Toepassingen</h2>
            <ul className="mt-3 space-y-2">
              {info.applications.map((a) => (
                <li
                  key={a}
                  className="flex gap-2 text-text-secondary before:mt-2 before:size-1.5 before:shrink-0 before:rounded-full before:bg-orange"
                >
                  {a}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mt-12">
          <h2 className="text-2xl font-bold tracking-tight">
            Veelgestelde vragen
          </h2>
          <div className="mt-4 divide-y divide-border rounded-xl border border-border bg-card">
            {faqs.map((f) => (
              <details key={f.q} className="group p-5">
                <summary className="cursor-pointer list-none font-semibold text-foreground">
                  {f.q}
                </summary>
                <p className="mt-2 text-text-secondary">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {info.slug === "beveiliging" && (
          <div className="mt-12 -mx-4 sm:-mx-6 lg:-mx-8">
            <SecurityPackages />
          </div>
        )}



        <section className="mt-12 rounded-2xl bg-card p-6 ring-1 ring-border sm:p-8">
          <h2 className="text-xl font-bold tracking-tight">
            Direct advies of een offerte?
          </h2>
          <p className="mt-2 text-text-secondary">
            Onze adviseurs staan voor je klaar. Vraag vrijblijvend advies of een
            offerte aan — wij denken graag met je mee.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to="/"
              hash="contact"
              className="inline-flex items-center rounded-md border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground hover:border-orange hover:text-orange"
            >
              Neem contact op
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
